/**
 * Organizations service
 * Handles CRUD operations for organizations
 */

import { supabase } from "./supabase";
import { fetchAllRows, pageByNameThenId } from "../utils/fetchAllRows";
import {
  getActiveVendorIndex,
  sanitizeSearchTerm,
  searchVendorOrganizationIds,
} from "./organizationVendor.service";
import type {
  Organization,
  OrganizationStats,
  OrganizationStatusHistory,
  OrganizationTypeValue,
  OrganizationLink,
  MapOrganization,
  MapLocation,
} from "../types/organization.types";
import type {
  UUID,
  PaginatedResponse,
  PaginationParams,
} from "../types/common.types";

// Extended pagination params with organization-specific filters
export interface OrganizationPaginationParams extends PaginationParams {
  status?: string;
  is_prospect?: boolean | null;
  organization_type?: OrganizationTypeValue | null;
  /** slice 2: "vendor" = has active vendor details; "business" = everything except vendor-only records */
  kind?: "business" | "vendor";
}

/** The list filters shared by the Organizations list and its CSV export. */
export type OrganizationListFilters = Pick<
  OrganizationPaginationParams,
  "search" | "status" | "is_prospect" | "organization_type" | "kind"
>;

/** The query-builder methods the list filters use (a PostgREST filter builder satisfies this). */
export interface OrganizationFilterableQuery {
  or(filters: string): this;
  eq(column: string, value: unknown): this;
  in(column: string, values: readonly unknown[]): this;
  not(column: string, operator: string, value: unknown): this;
}

/**
 * Resolve the Organizations list filters once (the search's vendor matches and the vendor index are
 * fetched here) and return a function that applies them to a query. The list (count + page) and the
 * CSV export both use this, so the export always contains exactly the rows the list shows.
 * The caller adds the `is_archived = false` scope, sort and range.
 * Strict: any failed lookup here (vendor search ids, vendor index) rejects; it is never treated as
 * an empty result, so neither the list nor the export can silently omit matching organizations.
 */
export const buildOrganizationListFilter = async (
  filters: OrganizationListFilters,
): Promise<<Q extends OrganizationFilterableQuery>(query: Q) => Q> => {
  const { search, status, is_prospect, organization_type, kind } = filters;
  const steps: Array<
    (q: OrganizationFilterableQuery) => OrganizationFilterableQuery
  > = [];

  // Search: the term is sanitized (PostgREST .or() syntax characters stripped). Slice 2 extends it to
  // legal name, Facility/Affiliate ID, account number and vendor name / number / email.
  const term = search ? sanitizeSearchTerm(search) : "";
  if (term) {
    // No .catch: a failed vendor lookup must fail the list and the export loudly. Treating it as
    // "no vendor matches" silently drops organizations from both (audit OE-1, 2026-10-06).
    const vendorIds = await searchVendorOrganizationIds(term);
    const parts = [
      "name",
      "legal_name",
      "city_tag",
      "state_tag",
      "address_city",
      "address_state",
      "facility_id",
      "affiliate_id",
      "account_number",
    ].map((c) => `${c}.ilike.*${term}*`);
    if (vendorIds.length > 0) {
      parts.push(`id.in.(${vendorIds.join(",")})`);
    }
    const searchFilter = parts.join(",");
    steps.push((q) => q.or(searchFilter));
  }

  // Kind filter (slice 2). Vendors are a small, bounded set (getActiveVendorIndex).
  if (kind) {
    const index = await getActiveVendorIndex();
    if (kind === "vendor") {
      const ids = index.map((v) => v.organization_id);
      const list = ids.length ? ids : ["00000000-0000-0000-0000-000000000000"];
      steps.push((q) => q.in("id", list));
    } else {
      const vendorOnly = index
        .filter((v) => v.vendor_only)
        .map((v) => v.organization_id);
      if (vendorOnly.length) {
        const notIn = `(${vendorOnly.join(",")})`;
        steps.push((q) => q.not("id", "in", notIn));
      }
    }
  }

  if (status && status !== "all") {
    steps.push((q) => q.eq("status", status));
  }
  if (is_prospect !== null && is_prospect !== undefined) {
    steps.push((q) => q.eq("is_prospect", is_prospect));
  }
  if (organization_type) {
    steps.push((q) => q.eq("organization_type", organization_type));
  }

  return <Q extends OrganizationFilterableQuery>(query: Q): Q =>
    steps.reduce<OrganizationFilterableQuery>((q, step) => step(q), query) as Q;
};

/**
 * Get all organizations with pagination
 */
export const getOrganizations = async (
  params: OrganizationPaginationParams,
): Promise<PaginatedResponse<Organization>> => {
  console.log(
    "[OrganizationsService] getOrganizations called with params:",
    params,
  );

  const {
    page = 1,
    limit = 10,
    sortBy = "created_at",
    sortOrder = "desc",
    search,
    status,
    is_prospect,
    organization_type,
    kind,
  } = params;
  const start = (page - 1) * limit;
  const end = start + limit - 1;

  console.log(
    "[OrganizationsService] Pagination: page=",
    page,
    "limit=",
    limit,
    "start=",
    start,
    "end=",
    end,
  );

  // Build base query
  let countQuery = supabase
    .from("organizations")
    .select("*", { count: "exact", head: true })
    .eq("is_archived", false);
  let dataQuery = supabase
    .from("organizations")
    .select("*")
    .eq("is_archived", false);

  // Filters are built once and applied to both queries; exportOrganizations uses the same builder so
  // the CSV export always matches what the list shows.
  const applyFilters = await buildOrganizationListFilter({
    search,
    status,
    is_prospect,
    organization_type,
    kind,
  });
  countQuery = applyFilters(countQuery);
  dataQuery = applyFilters(dataQuery);

  // Get total count
  console.log("[OrganizationsService] Fetching count...");
  const { count, error: countError } = await countQuery;

  if (countError) {
    console.error("[OrganizationsService] Count query error:", countError);
    throw countError;
  }

  console.log("[OrganizationsService] Total count:", count);

  // Get paginated data with sorting
  console.log(
    "[OrganizationsService] Fetching data with sortBy:",
    sortBy,
    "sortOrder:",
    sortOrder,
  );
  const { data, error } = await dataQuery
    .order(sortBy, { ascending: sortOrder === "asc" })
    .range(start, end);

  if (error) {
    console.error("[OrganizationsService] Data query error:", error);
    throw error;
  }

  console.log(
    "[OrganizationsService] Data fetched successfully. Records:",
    data?.length || 0,
  );
  console.log(
    "[OrganizationsService] First 3 organizations:",
    data?.slice(0, 3).map((o) => ({ id: o.id, name: o.name })),
  );

  return {
    data: data as Organization[],
    total: count || 0,
    page,
    limit,
    totalPages: Math.ceil((count || 0) / limit),
  };
};

/**
 * Get organization by ID
 */
export const getOrganization = async (id: UUID): Promise<Organization> => {
  const { data, error } = await supabase
    .from("organizations")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error("Organization not found");
  }

  return data as Organization;
};

/**
 * Check if an affiliate_id or facility_id is already in use by another organization.
 * Returns the name of the conflicting org, or null if no conflict.
 */
export const checkOrgFieldUniqueness = async (
  field: "affiliate_id" | "facility_id",
  value: string,
  excludeOrgId?: UUID,
): Promise<string | null> => {
  if (!value || !value.trim()) {
    return null;
  }
  let query = supabase
    .from("organizations")
    .select("id, name")
    .eq(field, value.trim())
    .limit(1)
    .single();
  if (excludeOrgId) {
    query = query.neq("id", excludeOrgId);
  }
  const { data } = await query;
  return data?.name || null;
};

/**
 * Facility-ID rule (migration 2026-10-25-996): a facility ID may belong to only ONE organization hierarchy -- the
 * organization and/or its own locations. Returns a description of the record in ANOTHER hierarchy that already holds
 * `value`, or null. Advisory only (it reads through the caller's RLS); the database trigger is authoritative.
 * Callers check only when the facility ID is being introduced or changed, so existing duplicates keep saving.
 * @param owningOrganizationId the organization the record belongs to (the org itself, or the location's parent);
 *                             undefined for a brand-new organization.
 */
export const findFacilityIdConflict = async (
  value: string,
  owningOrganizationId?: UUID,
): Promise<string | null> => {
  const v = (value ?? "").trim();
  if (!v) {
    return null;
  }
  const [orgRes, locRes] = await Promise.all([
    supabase
      .from("organizations")
      .select("id, name")
      .eq("facility_id", v)
      .limit(5),
    supabase
      .from("locations")
      .select("id, name, organization_id, organizations(name)")
      .eq("facility_id", v)
      .limit(5),
  ]);
  const org = (orgRes.data ?? []).find((o) => o.id !== owningOrganizationId);
  if (org) {
    return `organization "${org.name}"`;
  }
  const loc = (
    (locRes.data ?? []) as Array<{
      id: string;
      name: string;
      organization_id: string;
      organizations?: { name?: string } | { name?: string }[] | null;
    }>
  ).find((l) => l.organization_id !== owningOrganizationId);
  if (loc) {
    const parent = Array.isArray(loc.organizations)
      ? loc.organizations[0]?.name
      : loc.organizations?.name;
    return `location "${loc.name}"${parent ? ` of organization "${parent}"` : ""}`;
  }
  return null;
};

/** Message shown when a facility ID is refused (UI pre-check and the database trigger say the same thing). */
export const facilityIdConflictMessage = (value: string, holder: string) =>
  `Facility ID "${value.trim()}" is already used by ${holder}. A facility ID can belong to only one organization and its own locations. Clear it on the other record first, or ask the business owner which record should keep it.`;

/**
 * Create new organization
 */
export const createOrganization = async (
  organization: Omit<Organization, "id" | "created_at" | "updated_at">,
): Promise<Organization> => {
  const { data, error } = await supabase
    .from("organizations")
    .insert(organization)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as Organization;
};

/**
 * Update organization
 */
export const updateOrganization = async (
  id: UUID,
  updates: Partial<Organization>,
): Promise<Organization> => {
  const { data, error } = await supabase
    .from("organizations")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    throw error;
  }
  return data as Organization;
};

/**
 * Disable or restore an organization through the admin-gated lifecycle RPC.
 */
export const setOrganizationArchived = async (
  id: UUID,
  isArchived: boolean,
): Promise<Organization> => {
  const { data, error } = await supabase.rpc("set_organization_archived", {
    p_organization_id: id,
    p_archived: isArchived,
  });

  if (error) {
    throw error;
  }

  return data as Organization;
};

/** @deprecated Use setOrganizationArchived. */
export const deleteOrganization = async (id: UUID): Promise<void> => {
  await setOrganizationArchived(id, true);
};

/**
 * Hard delete organization (permanent)
 */
export const hardDeleteOrganization = async (id: UUID): Promise<void> => {
  const { error } = await supabase.from("organizations").delete().eq("id", id);

  if (error) {
    throw error;
  }
};

/**
 * Search organizations by name
 */
export const searchOrganizations = async (
  query: string,
): Promise<Organization[]> => {
  const { data, error } = await supabase
    .from("organizations")
    .select("*")
    .eq("is_archived", false)
    .or(
      `name.ilike.*${query}*,legal_name.ilike.*${query}*,email.ilike.*${query}*`,
    )
    .limit(20);

  if (error) {
    throw error;
  }
  return data as Organization[];
};

/**
 * Get organization statistics
 */
export const getOrganizationStats = async (
  organizationId: UUID,
): Promise<OrganizationStats> => {
  // Run parallel queries for different stats
  const [
    contactsResult,
    locationsResult,
    tasksResult,
    eventsResult,
    interactionsResult,
  ] = await Promise.all([
    supabase
      .from("contacts")
      .select("*", { count: "exact", head: true })
      .eq("organization_id", organizationId),
    supabase
      .from("locations")
      .select("*", { count: "exact", head: true })
      .eq("organization_id", organizationId),
    supabase
      .from("tasks")
      .select("*", { count: "exact", head: true })
      .eq("organization_id", organizationId)
      .in("status", ["to_do", "in_progress"]),
    supabase
      .from("events")
      .select("*", { count: "exact", head: true })
      .eq("organization_id", organizationId)
      .gte(
        "start_time",
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1,
        ).toISOString(),
      ),
    supabase
      .from("interactions")
      .select("*", { count: "exact", head: true })
      .eq("organization_id", organizationId)
      .gte(
        "interaction_date",
        new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1,
        ).toISOString(),
      ),
  ]);

  return {
    total_contacts: contactsResult.count || 0,
    total_locations: locationsResult.count || 0,
    total_active_tasks: tasksResult.count || 0,
    total_events_this_month: eventsResult.count || 0,
    total_interactions_this_month: interactionsResult.count || 0,
  };
};

/**
 * Get child organizations (for multi-level hierarchy)
 */
export const getChildOrganizations = async (
  parentId: UUID,
): Promise<Organization[]> => {
  const { data, error } = await supabase
    .from("organizations")
    .select("*")
    .eq("parent_organization_id", parentId)
    .eq("is_archived", false);

  if (error) {
    throw error;
  }
  return data as Organization[];
};

/**
 * Get organization status history
 * Returns all status/prospect/type changes for an organization
 */
export const getOrganizationStatusHistory = async (
  organizationId: UUID,
): Promise<OrganizationStatusHistory[]> => {
  // Query without the FK join since changed_by references auth.users not profiles
  const { data, error } = await supabase
    .from("organization_status_history")
    .select("*")
    .eq("organization_id", organizationId)
    .order("changed_at", { ascending: false });

  if (error) {
    // If table doesn't exist or FK issue, return empty array
    if (error.code === "42P01" || error.code === "PGRST200") {
      console.warn(
        "[OrganizationsService] organization_status_history issue:",
        error.message,
      );
      return [];
    }
    throw error;
  }

  return (data || []) as OrganizationStatusHistory[];
};

// =====================================================
// LINKED ORGANIZATIONS
// =====================================================

/**
 * Get all linked organizations for a given organization
 * Queries both sides of the link and resolves the "other" org
 */
export const getLinkedOrganizations = async (
  orgId: UUID,
): Promise<OrganizationLink[]> => {
  // Query links where this org is on either side
  const { data, error } = await supabase
    .from("organization_links")
    .select(
      `
      *,
      org_a:organization_a_id (id, name, organization_type, status, city_tag, state_tag),
      org_b:organization_b_id (id, name, organization_type, status, city_tag, state_tag)
    `,
    )
    .or(`organization_a_id.eq.${orgId},organization_b_id.eq.${orgId}`)
    .order("created_at", { ascending: false });

  if (error) {
    if (error.code === "42P01") {
      // Table doesn't exist yet
      return [];
    }
    throw error;
  }

  // Map to OrganizationLink with the "other" org resolved
  return (data || []).map((link: any) => ({
    id: link.id,
    organization_a_id: link.organization_a_id,
    organization_b_id: link.organization_b_id,
    description: link.description,
    created_at: link.created_at,
    updated_at: link.updated_at,
    linked_organization:
      link.organization_a_id === orgId
        ? (link.org_b as Organization)
        : (link.org_a as Organization),
  }));
};

/**
 * Create a link between two organizations
 */
export const linkOrganization = async (
  orgAId: UUID,
  orgBId: UUID,
  description?: string,
  createdBy?: UUID,
): Promise<void> => {
  // Sort IDs to ensure consistent uniqueness (a < b alphabetically)
  const [aId, bId] = [orgAId, orgBId].sort();

  const { error } = await supabase.from("organization_links").insert({
    organization_a_id: aId,
    organization_b_id: bId,
    description: description || null,
    created_by: createdBy || null,
  });

  if (error) {
    if (error.code === "23505") {
      throw new Error("These organizations are already linked");
    }
    throw error;
  }
};

/**
 * Remove a link between organizations
 */
export const unlinkOrganization = async (linkId: UUID): Promise<void> => {
  const { error } = await supabase
    .from("organization_links")
    .delete()
    .eq("id", linkId);

  if (error) {
    throw error;
  }
};

/**
 * Get just the metadata column for an organization (lightweight fetch for location inheritance)
 */
export const getOrganizationMetadata = async (
  orgId: UUID,
): Promise<Record<string, unknown>> => {
  const { data, error } = await supabase
    .from("organizations")
    .select("metadata")
    .eq("id", orgId)
    .single();

  if (error) {
    throw error;
  }
  return (data?.metadata as Record<string, unknown>) || {};
};

/**
 * Update the description on an organization link
 */
export const updateOrganizationLink = async (
  linkId: UUID,
  description: string,
): Promise<void> => {
  const { error } = await supabase
    .from("organization_links")
    .update({ description })
    .eq("id", linkId);

  if (error) {
    throw error;
  }
};

// =====================================================
// MAP VIEW FUNCTIONS
// =====================================================

export interface MapFilters {
  organization_type?: OrganizationTypeValue | null;
  activeOnly?: boolean;
}

/**
 * Get organizations with coordinates for map display.
 * Lightweight select — only fields needed for markers.
 */
export const getOrganizationsForMap = async (
  filters?: MapFilters,
): Promise<MapOrganization[]> => {
  // Paged: PROD has ~2,700 mappable organizations; a single select stopped at
  // PostgREST's 1000-row cap, so markers past ~#1000 by name never rendered.
  return fetchAllRows<MapOrganization>((from, to) => {
    let query = supabase
      .from("organizations")
      .select(
        "id, name, organization_type, status, is_prospect, latitude, longitude, address_line1, address_city, address_state, address_postal_code, phone",
      )
      .eq("is_archived", false)
      .not("latitude", "is", null)
      .not("longitude", "is", null);

    if (filters?.activeOnly !== false) {
      query = query.eq("status", "active");
    }
    if (filters?.organization_type) {
      query = query.eq("organization_type", filters.organization_type);
    }
    return pageByNameThenId(query, from, to) as unknown as PromiseLike<{
      data: MapOrganization[] | null;
      error: unknown;
    }>;
  });
};

/**
 * Search organizations by zip code + radius using the DB RPC function.
 * Returns results with distance_in_miles.
 */
export const searchOrganizationsByZip = async (
  zipLat: number,
  zipLng: number,
  radiusMiles: number,
  filters?: MapFilters,
): Promise<{ total: number; organizations: MapOrganization[] }> => {
  const { data, error } = await supabase.rpc(
    "search_organizations_by_location",
    {
      p_lat: zipLat,
      p_lng: zipLng,
      p_radius_miles: radiusMiles,
      p_organization_type: filters?.organization_type || null,
      p_page_size: 1000,
      p_page: 1,
    },
  );

  if (error) {
    throw error;
  }

  const result = data as { total: number; organizations: MapOrganization[] };
  return {
    total: result.total,
    organizations: result.organizations || [],
  };
};

/**
 * Look up a zip code's coordinates from the us_zip_codes table.
 */
export const getZipCodeCoordinates = async (
  zip: string,
): Promise<{ lat: number; lng: number } | null> => {
  const { data, error } = await supabase
    .from("us_zip_codes")
    .select("lat, lng")
    .eq("zip", zip.substring(0, 5))
    .limit(1)
    .single();

  if (error) {
    return null;
  }
  return data as { lat: number; lng: number };
};

/**
 * Get locations with coordinates for map display.
 * Gracefully returns [] if the locations table lacks lat/lng columns.
 */
export const getLocationsForMap = async (
  orgIds?: string[],
  activeOnly: boolean = true,
): Promise<MapLocation[]> => {
  try {
    let query = supabase
      .from("locations")
      .select(
        "id, organization_id, name, latitude, longitude, city, state, postal_code, address_line1, is_archived, organizations!inner(is_archived)",
      )
      .eq("organizations.is_archived", false)
      .not("latitude", "is", null)
      .not("longitude", "is", null);

    if (activeOnly) {
      query = query.neq("is_archived", true);
    }
    if (orgIds && orgIds.length > 0) {
      query = query.in("organization_id", orgIds);
    }

    const { data, error } = await query.order("name");

    if (error) {
      // Column may not exist yet — return empty gracefully
      console.warn(
        "[getLocationsForMap] Query failed, returning empty:",
        error.message,
      );
      return [];
    }
    return (data || []) as MapLocation[];
  } catch {
    return [];
  }
};

// =====================================================
// BUSINESS PAGE: DUPLICATE CANDIDATES + EXTERNAL LINKS
// =====================================================

export interface DuplicateCandidateInput {
  name?: string;
  legal_name?: string;
  email?: string;
  phone?: string;
  facility_id?: string;
  affiliate_id?: string;
  account_number?: string;
  exclude_id?: UUID;
}

export type DuplicateMatchReason =
  | "name"
  | "email"
  | "phone"
  | "facility_id"
  | "affiliate_id"
  | "account_number";

export interface DuplicateCandidate {
  id: UUID;
  name: string;
  legal_name: string | null;
  address_city: string | null;
  address_state: string | null;
  status: string | null;
  is_archived: boolean;
  match_reasons: DuplicateMatchReason[];
}

/**
 * Possible duplicates for the create form (warn-only; migration 2026-10-05-302). Normalization happens in the
 * database: case/punctuation/"&"/LLC/Inc ignored for names, last 10 digits for phones, case-insensitive email/IDs.
 */
export const findOrganizationDuplicateCandidates = async (
  input: DuplicateCandidateInput,
): Promise<DuplicateCandidate[]> => {
  const nz = (v?: string) => (v && v.trim() ? v.trim() : null);
  const { data, error } = await supabase.rpc(
    "find_organization_duplicate_candidates",
    {
      p_name: nz(input.name),
      p_legal_name: nz(input.legal_name),
      p_email: nz(input.email),
      p_phone: nz(input.phone),
      p_facility_id: nz(input.facility_id),
      p_affiliate_id: nz(input.affiliate_id),
      p_account_number: nz(input.account_number),
      p_exclude_id: input.exclude_id ?? null,
    },
  );
  if (error) {
    throw error;
  }
  return (data ?? []) as DuplicateCandidate[];
};

export interface OrganizationExternalLink {
  id: UUID;
  organization_id: UUID;
  name: string;
  url: string;
  position: number | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: UUID | null;
}

/** Labels offered by "+ Add Link"; "Other" lets the operator type a label. */
export const EXTERNAL_LINK_PRESETS = ["Agent Portal", "Admin Portal"] as const;

/** http(s) only -- mirrors CHECK organization_external_links_url_http. */
export const isValidExternalLinkUrl = (url: string): boolean =>
  /^https?:\/\/\S+$/i.test(url.trim()) && url.trim().length <= 2048;

export const getOrganizationExternalLinks = async (
  organizationId: UUID,
): Promise<OrganizationExternalLink[]> => {
  const { data, error } = await supabase
    .from("organization_external_links")
    .select("*")
    .eq("organization_id", organizationId)
    .order("position", { ascending: true })
    .order("created_at", { ascending: true })
    .limit(200);
  if (error) {
    throw error;
  }
  return (data ?? []) as OrganizationExternalLink[];
};

export const createOrganizationExternalLink = async (link: {
  organization_id: UUID;
  name: string;
  url: string;
  position?: number;
}): Promise<OrganizationExternalLink> => {
  const { data, error } = await supabase
    .from("organization_external_links")
    .insert({
      organization_id: link.organization_id,
      name: link.name.trim(),
      url: link.url.trim(),
      position: link.position ?? 0,
    })
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as OrganizationExternalLink;
};

export const updateOrganizationExternalLink = async (
  id: UUID,
  updates: { name?: string; url?: string },
): Promise<OrganizationExternalLink> => {
  const payload: Record<string, string> = {};
  if (updates.name !== undefined) {
    payload.name = updates.name.trim();
  }
  if (updates.url !== undefined) {
    payload.url = updates.url.trim();
  }
  const { data, error } = await supabase
    .from("organization_external_links")
    .update(payload)
    .eq("id", id)
    .select()
    .single();
  if (error) {
    throw error;
  }
  return data as OrganizationExternalLink;
};

export const deleteOrganizationExternalLink = async (
  id: UUID,
): Promise<void> => {
  const { error } = await supabase
    .from("organization_external_links")
    .delete()
    .eq("id", id);
  if (error) {
    throw error;
  }
};
